import React, { useState, useEffect } from "react";
import {
  getLinkhubSettings,
  updateLinkhubSettings,
  LinkhubSettings,
} from "../../services/api/admin/linkhub";
import {
  Save,
  Loader2,
  Link as LinkIcon,
  Instagram,
  MessageCircle,
  ExternalLink,
  Globe,
  Users,
  GraduationCap,
  Ticket,
} from "lucide-react";
import Swal from "sweetalert2";

export const LinkhubManager: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<LinkhubSettings>({
    socialGiz: { instagram: "", tiktok: "", whatsapp: "" },
    socialRiw: { instagram: "", tiktok: "", whatsapp: "" },
    links: {
      official_website: "",
      tiket_com: "",
      booking_rombongan: "",
      rabbit_goes_to_school: "",
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const data = await getLinkhubSettings();
      setSettings({
        socialGiz: {
          instagram: data.socialGiz?.instagram || "",
          tiktok: data.socialGiz?.tiktok || "",
          whatsapp: data.socialGiz?.whatsapp || "",
        },
        socialRiw: {
          instagram: data.socialRiw?.instagram || "",
          tiktok: data.socialRiw?.tiktok || "",
          whatsapp: data.socialRiw?.whatsapp || "",
        },
        links: {
          official_website: data.links?.official_website || "",
          tiket_com: data.links?.tiket_com || "",
          booking_rombongan: data.links?.booking_rombongan || "",
          rabbit_goes_to_school: data.links?.rabbit_goes_to_school || "",
        },
      });
    } catch (error) {
      console.error("Failed to fetch linkhub settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateLinkhubSettings(settings);
      Swal.fire({
        icon: "success",
        title: "Berhasil Disimpan",
        text: "Pengaturan LinkHub telah diperbarui",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to save linkhub settings:", error);
      Swal.fire({
        icon: "error",
        title: "Gagal Menyimpan",
        text: "Terjadi kesalahan saat menyimpan pengaturan",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const TikTokIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
    </svg>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 text-gray-400">
        <Loader2 className="w-10 h-10 animate-spin mb-4 text-indigo-500" />
        <p className="font-bold font-mono uppercase tracking-widest text-xs">
          Loading Settings...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Pengaturan LinkHub
          </h2>
          <p className="text-gray-500 text-sm">
            Kelola link dan social media untuk halaman LinkHub
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-slate-800 transition-all flex items-center gap-2 disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {isSaving ? "Menyimpan..." : "Simpan Perubahan"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GIZ Park Social Media */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center text-white text-xs font-black">
                GIZ
              </div>
              Social Media - GIZ Park
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                <Instagram className="w-4 h-4 text-pink-500" />
                Instagram
              </label>
              <input
                type="url"
                placeholder="https://instagram.com/giz.park"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                value={settings.socialGiz.instagram || ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    socialGiz: {
                      ...settings.socialGiz,
                      instagram: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                <TikTokIcon />
                TikTok
              </label>
              <input
                type="url"
                placeholder="https://tiktok.com/@giz.park"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                value={settings.socialGiz.tiktok || ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    socialGiz: {
                      ...settings.socialGiz,
                      tiktok: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                <MessageCircle className="w-4 h-4 text-emerald-500" />
                WhatsApp
              </label>
              <input
                type="url"
                placeholder="https://wa.me/628123456789"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                value={settings.socialGiz.whatsapp || ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    socialGiz: {
                      ...settings.socialGiz,
                      whatsapp: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>
        </div>

        {/* Rabbit in Wonderland Social Media */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-pink-50 to-purple-50">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-pink-500 flex items-center justify-center text-white text-xs font-black">
                RIW
              </div>
              Social Media - Rabbit in Wonderland
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                <Instagram className="w-4 h-4 text-pink-500" />
                Instagram
              </label>
              <input
                type="url"
                placeholder="https://instagram.com/rabbitinwonderland"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none"
                value={settings.socialRiw.instagram || ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    socialRiw: {
                      ...settings.socialRiw,
                      instagram: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                <TikTokIcon />
                TikTok
              </label>
              <input
                type="url"
                placeholder="https://tiktok.com/@rabbitinwonderland"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none"
                value={settings.socialRiw.tiktok || ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    socialRiw: {
                      ...settings.socialRiw,
                      tiktok: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                <MessageCircle className="w-4 h-4 text-emerald-500" />
                WhatsApp
              </label>
              <input
                type="url"
                placeholder="https://wa.me/628123456789"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none"
                value={settings.socialRiw.whatsapp || ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    socialRiw: {
                      ...settings.socialRiw,
                      whatsapp: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Link Cards Settings */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-gray-50">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-indigo-500" />
            Pengaturan Link Cards
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            URL untuk setiap tombol di halaman LinkHub
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              <Globe className="w-4 h-4 text-blue-500" />
              Official Website
            </label>
            <input
              type="url"
              placeholder="https://www.gizrabbitland.com"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              value={settings.links.official_website || ""}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  links: {
                    ...settings.links,
                    official_website: e.target.value,
                  },
                })
              }
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              <Ticket className="w-4 h-4 text-yellow-500" />
              Tiket.com
            </label>
            <input
              type="url"
              placeholder="https://www.tiket.com/event/..."
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              value={settings.links.tiket_com || ""}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  links: { ...settings.links, tiket_com: e.target.value },
                })
              }
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              <Users className="w-4 h-4 text-emerald-500" />
              Booking Rombongan (WhatsApp)
            </label>
            <input
              type="url"
              placeholder="https://wa.me/628123456789"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              value={settings.links.booking_rombongan || ""}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  links: {
                    ...settings.links,
                    booking_rombongan: e.target.value,
                  },
                })
              }
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              <GraduationCap className="w-4 h-4 text-purple-500" />
              Rabbit Goes to School (WhatsApp)
            </label>
            <input
              type="url"
              placeholder="https://wa.me/628123456789"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              value={settings.links.rabbit_goes_to_school || ""}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  links: {
                    ...settings.links,
                    rabbit_goes_to_school: e.target.value,
                  },
                })
              }
            />
          </div>
        </div>
      </div>

      {/* Preview Link */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Preview LinkHub</h3>
            <p className="text-sm text-gray-500">
              Lihat tampilan halaman LinkHub saat ini
            </p>
          </div>
          <a
            href="https://link.gizrabbitland.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-xl font-bold text-sm text-indigo-600 hover:bg-indigo-50 transition-colors border border-indigo-200"
          >
            <ExternalLink className="w-4 h-4" />
            Buka LinkHub
          </a>
        </div>
      </div>
    </div>
  );
};
